import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useCatalog } from '@/api/catalog';
import { api, ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import type { CategoryRow, PurchaseStatus, SupplierOption, Warehouse, WithStats } from './types';

/** 盘点最终写入 V1 共用库存与流水；生产包由 V2 写闸门拦截。 */
export function createInventoryCountTask(payload: { title: string; warehouseId: string; scope: 'negative' | 'all'; blind: boolean; dueAt?: string }) {
  return api.post<{ id: string }>('/api/v2/inventory/count-tasks', payload);
}
export function saveInventoryCountLine(taskId: string, lineId: string, quantity: number) {
  return api.patch(`/api/v2/inventory/count-tasks/${encodeURIComponent(taskId)}/lines/${encodeURIComponent(lineId)}`, { quantity });
}
export function applyInventoryCountTask(taskId: string) {
  return api.post<{ changed: number; total: number }>(`/api/v2/inventory/count-tasks/${encodeURIComponent(taskId)}`, {});
}
export function cancelInventoryCountTask(taskId: string) {
  return api.delete(`/api/v2/inventory/count-tasks/${encodeURIComponent(taskId)}`);
}

/** V2 本地采购入库复用 V1 的采购行、库存流水和状态事务。 */
export function receivePurchaseOrder(id: string, payload: {
  lines: { lineId: string; quantity: number }[];
  note?: string;
}) {
  return api.post<{ purchaseOrderId: string; receivedQuantity: number; status: PurchaseStatus }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/receive`, payload);
}

export function createPurchaseAdjustment(id: string, payload: { kind: 'REJECTION' | 'RETURN'; lineId: string; quantity: number; reason: string }) {
  return api.post<{ id: string; status: PurchaseStatus }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/adjustments`, payload);
}

/** 每次只确认一张供应商/仓库采购单；成功后才进入下一张。 */
export function createPurchaseOrder(payload: {
  supplierId: string;
  warehouseId: string;
  orderDate?: string;
  expectedArrivalDate?: string;
  note?: string;
  lines: { productId: string; packagingOptionId: string; supplierQuoteId?: string; packageQuantity?: number; quantity: number; unitCost: number; note?: string; sourceOrderNos?: string[] }[];
}) {
  return api.post<{ id: string; purchaseNo: string }>('/api/v2/purchase-orders', payload);
}

export function createPurchaseOrderDraft(payload: Parameters<typeof createPurchaseOrder>[0]) {
  return api.post<{ id: string; purchaseNo: string }>('/api/v2/purchase-orders/draft', payload);
}

export function editPurchaseOrderDraft(id: string, payload: Parameters<typeof createPurchaseOrder>[0] & { expectedUpdatedAt: string }) {
  return api.patch<{ id: string; purchaseNo: string; status: PurchaseStatus; updatedAt: string }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}`, payload);
}

export function submitPurchaseOrderDraft(id: string) {
  return api.post<{ id: string; purchaseNo: string; status: PurchaseStatus }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/submit`, {});
}

export function generatePurchaseOrderPdf(id: string) {
  return api.post<{ pdfFileUrl?: string | null; pdfFileName?: string | null }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/pdf`, {});
}

export function adjustInventory(payload: { productId: string; warehouseId: string; mode: 'set' | 'delta'; quantity: number; note: string }) {
  return api.post<{ success: boolean; transactionId?: string }>('/api/v2/inventory/transactions', payload);
}

export function manualInbound(payload: { productId: string; warehouseId: string; packagingOptionId: string; supplierId?: string; quantity: number; packageQuantity?: number; unitCost: number; note?: string }) {
  return api.post<{ success: boolean; transactionId: string }>('/api/v2/inventory/transactions', { mode: 'in', ...payload });
}

export function manualOutbound(payload: { productId: string; warehouseId: string; quantity: number; note: string; exceptionId?: string }) {
  return api.post<{ success: boolean; transactionId?: string; exceptionId?: string }>('/api/v2/inventory/transactions', { mode: 'out', ...payload });
}

export type InitialStockImportResult = {
  importJobId: string;
  totalRows: number;
  successRows: number;
  failedRows: number;
  mode: 'set' | 'add';
  replay: boolean;
  errors: { rowNumber: number; message: string; rowData: Record<string, string> }[];
};

export function importInitialStock(file: File, mode: 'set' | 'add', requestKey: string) {
  const form = new FormData();
  form.set('file', file);
  form.set('mode', mode);
  form.set('requestKey', requestKey);
  return api.postForm<InitialStockImportResult>('/api/v2/import/initial-stock', form);
}

/** 仅部分入库且仍有剩余时可结案，服务端按 V1 规则重新核对。 */
export function closePurchaseOrderRemaining(id: string) {
  return api.post<{ id: string; status: PurchaseStatus }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/close-remaining`, {});
}

/** 仅未入库、未进入结算的采购单可删除，服务端按 V1 规则重新核对。 */
export function deletePurchaseOrder(id: string) {
  return api.delete<{ id: string }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}`);
}

type Query = Record<string, string | number | boolean | undefined>;

/* ------------------------------ URL 状态 ------------------------------ */

/** 视图、筛选、搜索、分页都写进 URL 查询参数（简报 §5）。改参数用 replace，不堆历史 */
export function useUrlState() {
  const [sp, setSp] = useSearchParams();
  const set = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSp(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) next.set(k, v);
            else next.delete(k);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSp],
  );
  return [sp, set] as const;
}

/* ------------------------------ 分页列表 ------------------------------ */

type ListState<T, S> = {
  items: T[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  stats?: S;
  loading: boolean;
  error?: ApiError | Error;
};

/**
 * 通用分页列表。
 * - append=true（手机）：条件变了回到第一页，「加载更多」追加；
 * - append=false（iPad / 桌面）：按传入的 page 取一页（page 来自 URL）。
 * 旧请求 abort 掉，避免竞态。path 为 null 表示没权限，不发请求。
 */
export function usePagedList<T, S = undefined>(path: string | null, query: Query, opts: { pageSize: number; append: boolean; page?: number }) {
  const [state, setState] = useState<ListState<T, S>>({ items: [], total: 0, page: 1, totalPages: 1, hasNext: false, loading: !!path });
  const ctrl = useRef<AbortController | null>(null);
  const lastFilter = useRef<string | null>(null);
  const key = JSON.stringify(query);
  const { pageSize, append } = opts;
  const urlPage = append ? 1 : Math.max(1, opts.page || 1);

  const fetchPage = useCallback(
    async (page: number, reset: boolean) => {
      if (!path) return;
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      const filter = JSON.stringify([path, key, pageSize, append ? 1 : page]);
      const changed = lastFilter.current !== filter;
      lastFilter.current = filter;
      setState((s) => ({ ...s, loading: true, error: undefined, ...(reset && changed ? { items: [], stats: undefined } : {}) }));
      try {
        const data = await api.get<WithStats<T, S>>(path, { ...JSON.parse(key), page, pageSize }, c.signal);
        setState((s) => ({
          items: reset || !append ? data.items : [...s.items, ...data.items],
          total: data.pagination.total,
          page: data.pagination.page,
          totalPages: data.pagination.totalPages,
          hasNext: data.pagination.hasNext,
          stats: data.stats ?? (reset ? undefined : s.stats),
          loading: false,
        }));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    },
    [path, key, pageSize, append],
  );

  useEffect(() => {
    fetchPage(urlPage, true);
    return () => ctrl.current?.abort();
  }, [fetchPage, urlPage]);

  useEffect(() => {
    if (!path) return;
    const refresh = () => void fetchPage(urlPage, true);
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, [fetchPage, path, urlPage]);

  return {
    ...state,
    reload: () => fetchPage(append ? 1 : urlPage, true),
    loadMore: () => {
      if (state.hasNext && !state.loading) fetchPage(state.page + 1, false);
    },
  };
}

/** 单个资源（详情、按产品的分仓库存等） */
export function useResource<T>(path: string | null) {
  const [state, setState] = useState<{ path: string | null; data?: T; loading: boolean; error?: Error }>({ path, loading: !!path });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!path) return;
    const refresh = () => setNonce((value) => value + 1);
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, [path]);
  useEffect(() => {
    if (!path) {
      setState({ path, loading: false });
      return;
    }
    const c = new AbortController();
    setState((current) => current.path === path ? { ...current, loading: true, error: undefined } : { path, loading: true });
    api
      .get<T>(path, undefined, c.signal)
      .then((data) => setState({ path, data, loading: false }))
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setState((current) => current.path === path
          ? { ...current, loading: false, error: e as Error }
          : { path, loading: false, error: e as Error });
      });
    return () => c.abort();
  }, [path, nonce]);
  const visible: { data?: T; loading: boolean; error?: Error } = state.path === path ? state : { loading: !!path };
  return { ...visible, reload: () => setNonce((n) => n + 1) };
}

/* ------------------------------ 最新完整资料 ------------------------------ */
export function useWarehouses(enabled = true) { return useCatalog<Warehouse>(enabled ? '/api/warehouses' : null).data; }
export function useSuppliers(enabled = true) { return useCatalog<SupplierOption>(enabled ? '/api/suppliers?scope=purchase-options' : null).data; }
export function useInboundSuppliers(enabled = true) { return useCatalog<SupplierOption>(enabled ? '/api/suppliers?scope=inventory-inbound' : null).data; }
export function useCategories(enabled = true) { return useCatalog<CategoryRow>(enabled ? '/api/inventory/summary?group=category' : null).data; }

/* ------------------------------ 采购单各状态计数 ------------------------------ */

const PURCHASE_COUNT_KEYS: ('all' | PurchaseStatus)[] = ['all', 'ORDERED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'DRAFT', 'CANCELLED'];

/**
 * 老接口没有 stats：每个状态各发一次 pageSize=1 的 GET，只读 pagination.total。
 * 计数带上当前的搜索和筛选（不含状态本身），与列表口径一致。
 */
export function usePurchaseCounts(query: Query, enabled: boolean) {
  const [counts, setCounts] = useState<Partial<Record<'all' | PurchaseStatus, number>>>({});
  const [revision, setRevision] = useState(0);
  const key = JSON.stringify(query);
  useEffect(() => {
    if (!enabled) return;
    const c = new AbortController();
    Promise.all(
      PURCHASE_COUNT_KEYS.map((st) =>
        api
          .get<Page<unknown>>('/api/purchase-orders', { ...JSON.parse(key), status: st === 'all' ? undefined : st, page: 1, pageSize: 1 }, c.signal)
          .then((p) => [st, p.pagination.total] as const)
          .catch(() => [st, undefined] as const),
      ),
    ).then((rows) => {
      if (!c.signal.aborted) setCounts(Object.fromEntries(rows.filter(([, n]) => n !== undefined)));
    });
    return () => c.abort();
  }, [key, enabled, revision]);
  return { counts, reload: () => setRevision((value) => value + 1) };
}

/* ------------------------------ 日期 ------------------------------ */

/** 本地日期 YYYY-MM-DD（老系统 daysAgo 的写法：服务端 parseDate 按这一天的 0 点 / 23:59 取边界） */
export function daysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function adjustPurchaseQuantities(id: string, payload: { expectedUpdatedAt: string; lines: { id: string; quantity: number; packageQuantity: number | null }[] }) { return api.patch(`/api/v2/purchase-orders/${encodeURIComponent(id)}/quantities`, payload); }
